/**
 * 自机：移动/射击/炸弹/特殊/中弹/deathbomb/擦弹/死亡复活。
 * 角色三件套（shot/bomb/special）由内容层注入，机制层只做调用。
 * node 可直接 import（draw 才接触 renderer/Assets）。
 */
import { VIEW_W, VIEW_H } from '../config.js';
import { clamp } from '../engine/math.js';
import { SCORE_CONFIG } from './scoring.js';

/** 判定半径（px） */
const HITBOX_R = 2.5;
/** 高速移动速度（px/帧 @60fps） */
const SPEED_FAST = 4.5;
/** 低速移动速度（px/帧 @60fps） */
const SPEED_SLOW = 1.8;
/** 低速时显示的磁吸范围圈半径（px，与 items 磁吸半径一致） */
const MAGNET_RADIUS = 64;
/** deathbomb 窗口（秒，8 帧） */
const DEATHBOMB_WINDOW = 8 / 60;
/** 死亡复活后无敌时长（秒） */
const RESPAWN_INVULN = 2;
/** 炸弹无敌时长（秒） */
const BOMB_INVULN = 3;
/** 死亡火力损失比例 */
const POWER_LOSS_RATIO = 0.25;
/** 擦弹转化的特殊槽 */
const SPECIAL_PER_GRAZE = 0.5;
/** 活动边界内边距（px） */
const EDGE = 8;

export class Player {
  constructor() {
    this.x = 0;
    this.y = 0;
    /** 判定半径 */
    this.r = HITBOX_R;
    this.speedFast = SPEED_FAST;
    this.speedSlow = SPEED_SLOW;
    /** 无敌截止时刻（比较 this.time） */
    this.invulnUntil = 0;
    this.alive = true;
    /** 炸弹演出剩余时间（秒，>0 时可用于画面演出） */
    this.bombAnim = 0;
    /** 是否按住低速（本帧，draw 用） */
    this.slow = false;
    /** 本机时钟（秒），无敌/窗口比较基准 */
    this.time = 0;
    /** @type {object|null} 角色定义（含 shot/bomb/special/spriteKey） */
    this.character = null;
    this._pendingDeath = -1; // 中弹时刻；-1 = 未中弹
    this._hitOut = []; // 查询复用数组，零分配
    this._grazeOut = [];
    this._nextShotAt = 0; // 射击限速时间戳（characters.js makeShot 以 this.time 为基准）
  }

  /**
   * 重置到出生状态。
   * @param {number} x @param {number} y
   * @param {object|null} charDef 角色定义
   */
  reset(x, y, charDef) {
    this._trail = null; // 冲刺残影状态不带入新一局（审计发现）
    this._trailT = 0;
    this.x = x;
    this.y = y;
    this.character = charDef;
    // 角色基础性能（characters.js stats；缺省用基准值）
    const st = (charDef && charDef.stats) || {};
    this.baseSpeedFast = st.speedFast || SPEED_FAST; // 基准高速（dash 结束后恢复用）
    this.speedFast = this.baseSpeedFast;
    this.speedSlow = st.speedSlow || SPEED_SLOW;
    this.r = st.hitbox || HITBOX_R;
    this.magnetR = st.magnet || MAGNET_RADIUS;
    this.alive = true;
    this.bombAnim = 0;
    this.slow = false;
    this.time = 0;
    this.invulnUntil = RESPAWN_INVULN;
    this._pendingDeath = -1;
    // time 归零必须连同射击限速时间戳一起清，否则重开后长时间无法射击
    this._nextShotAt = 0;
  }

  /**
   * 每帧推进。
   * @param {number} dt 秒
   * @param {object} ctx {Input, state, field, playerShots, enemies, items, effects, audio}
   */
  update(dt, ctx) {
    if (!this.alive) return;
    this.time += dt;
    if (this.bombAnim > 0) this.bombAnim = Math.max(0, this.bombAnim - dt);
    const Input = ctx.Input;
    const state = ctx.state;

    // 移动 + 边界（_lean 平滑倾斜量，draw 按横向速度侧倾）
    const ax = Input.axis();
    this.slow = Input.isDown('slow');
    // 燃燃·全力应援：自机移速×1.15（与狂暴的纯输出差异化）
    const boostMul = this.mods && this.mods.optionBoostT > 0 ? 1.15 : 1;
    const sp = (this.slow ? this.speedSlow : this.speedFast) * boostMul * dt * 60;
    this.x = clamp(this.x + ax.x * sp, EDGE, VIEW_W - EDGE);
    this.y = clamp(this.y + ax.y * sp, EDGE, VIEW_H - EDGE);
    this._lean = (this._lean || 0) + (ax.x - (this._lean || 0)) * Math.min(1, dt * 14);

    // 射击（按住连发，弹道由角色定义决定）
    if (Input.isDown('shot') && this.character && this.character.shot) {
      this.character.shot.spawn(ctx.playerShots, this, this.slow, state.power);
    }

    // 炸弹（含 deathbomb：窗口内按 X 免死；窗口过期不扣雷不发动——历史 bug：白扣一颗还照死）
    if (Input.pressed('bomb') && state.bombs > 0) {
      const inWindow = this._pendingDeath >= 0 && this.time - this._pendingDeath <= DEATHBOMB_WINDOW;
      if (this._pendingDeath < 0 || inWindow) {
        if (inWindow) this._pendingDeath = -1; // 免死
        state.bombs--;
        this.bombAnim = 1;
        this.invulnUntil = this.time + BOMB_INVULN;
        if (this.character && this.character.bomb) this.character.bomb.activate(ctx);
        if (ctx.audio) ctx.audio.playSfx('bomb');
      }
      // 窗口过期：本次按 X 无效，让后续 _die 直接结算
    }

    // 特殊能力（槽满 100 才能放）
    if (Input.pressed('special') && this.character && this.character.special) {
      if (state.consumeSpecial()) {
        this.character.special.activate(ctx);
        if (ctx.audio) ctx.audio.playSfx('special');
        // 启动特效：闪光 + 金色迸发（技能启动必须可感知）
        if (ctx.effects) {
          ctx.effects.flash(0.25);
          ctx.effects.sparkle(this.x, this.y, '#fc3');
        }
      }
    }

    // 中弹检测（无敌期跳过）
    if (this.time >= this.invulnUntil && this._pendingDeath < 0) {
      const hits = ctx.field.queryHit(this.x, this.y, this.r, this._hitOut);
      let bodyHits = ctx.enemies ? ctx.enemies.queryHit(this.x, this.y, this.r) : null;
      // boss 体术判定（小半径，见 gameScene 注入 ctx.boss）
      const b = ctx.boss;
      if ((!bodyHits || bodyHits.length === 0) && b && b.alive) {
        const rr = this.r + b.r * 0.55;
        const dx = b.x - this.x;
        const dy = b.y - this.y;
        if (dx * dx + dy * dy <= rr * rr) bodyHits = [b];
      }
      if (hits.length > 0 || (bodyHits && bodyHits.length > 0)) {
        this._pendingDeath = this.time; // 开启 deathbomb 窗口
      }
    }
    // deathbomb 窗口到期才真正死亡
    if (this._pendingDeath >= 0 && this.time - this._pendingDeath > DEATHBOMB_WINDOW) {
      this._die(ctx);
    }

    // 擦弹检测：进入擦弹圈未命中的弹，每弹计一次
    const grazed = ctx.field.queryGraze(this.x, this.y, SCORE_CONFIG.grazeRadius, this._grazeOut);
    for (let i = 0; i < grazed.length; i++) {
      if (state.scoring.registerGraze(grazed[i].id)) {
        state.addSpecial(SPECIAL_PER_GRAZE);
        // 擦弹视觉反馈：被擦弹位迸一簇白色火花（此前只有音效——用户反馈；
        // 初版 2 粒 1px 实际不可见，加强到清晰可辨的规模）
        if (ctx.effects) ctx.effects.hitSpark(grazed[i].x, grazed[i].y, '#fff', { n: 6, sizeMul: 2.0, lifeMul: 2.0 });
      }
    }
  }

  /**
   * 死亡处理：扣残机/清火力 1/4/爆效，回底部出生点复活 + 2s 无敌闪烁；
   * 残机耗尽则 alive=false（场景层接管 gameover 流程）。
   * @param {object} ctx
   * @private
   */
  _die(ctx) {
    this._pendingDeath = -1;
    const gameover = ctx.state.loseLife();
    const powerBefore = ctx.state.power;
    ctx.state.power = Math.floor(ctx.state.power * (1 - POWER_LOSS_RATIO));
    const lostPower = powerBefore - ctx.state.power;
    const deathX = this.x;
    const deathY = this.y;
    if (ctx.effects) {
      ctx.effects.explosion(this.x, this.y, 2);
      ctx.effects.shake(20, 4);
      // 死亡必须可读：白闪 + MISS 大字
      ctx.effects.flash(0.6);
      ctx.effects.floatText(this.x, this.y - 36, 'MISS!', '#f66');
    }
    if (ctx.audio) ctx.audio.playSfx('death');
    if (gameover) {
      this.alive = false;
    } else {
      // 复活回到底部出生点（东方惯例）
      this.x = VIEW_W / 2;
      this.y = VIEW_H - 80;
      this.invulnUntil = this.time + RESPAWN_INVULN;
      if (ctx.effects) ctx.effects.sparkle(this.x, this.y, '#6cf'); // 复活闪光
      // 损失火力的 1/3 掉落为 P（死亡点周围，可拾回补偿）
      if (ctx.items && lostPower > 0) {
        const n = Math.min(5, Math.max(1, Math.floor(lostPower / 8 / 3)));
        for (let i = 0; i < n; i++) ctx.items.spawn(deathX, deathY, 'P');
      }
    }
  }

  /**
   * 绘制：精灵（无素材画占位圆）+ 低速判定点 + 磁吸圈；无敌期闪烁。
   * @param {import('../engine/renderer.js').Renderer} renderer
   * @param {import('../engine/assets.js').Assets} [assets]
   */
  draw(renderer, assets) {
    if (!this.alive) return;
    const invuln = this.time < this.invulnUntil;
    // 像素美学的无敌表达：硬闪烁（可见/隐藏逐窗切换），收尾前频率加快
    const left = this.invulnUntil - this.time;
    const freq = invuln && left < 0.6 ? 24 : 14;
    if (invuln && Math.floor(this.time * freq) % 2 === 0) return; // 本窗口隐身
    // 移动帧：按平滑倾斜量选手绘像素帧（左/右倾），静止回普通帧（像素风，不用旋转）
    const lean = this._lean || 0;
    const dir = lean > 0.3 ? '-r' : lean < -0.3 ? '-l' : '';
    const base = this.character && this.character.spriteKey;
    const img = assets && base ? assets.img(dir ? base + dir : base) || assets.img(base) : undefined;
    const m = this.mods || {};
    // 冲刺残影：同一像素帧按历史位置渐隐叠印（像素帧本身即特效）
    if (m.dashT > 0 && img && this._trail) {
      for (const tr of this._trail) {
        renderer.sprite(tr.img, tr.x, tr.y, { alpha: tr.alpha, scale: 0.5 });
      }
    }
    // buff 换色：状态直接画在角色身上（不叠任何 UI 圆圈/滤镜）
    let tintColor = null;
    if (m.rageT > 0) tintColor = '#f44';
    else if (m.optionBoostT > 0) tintColor = '#fc3';
    else if (m.shieldT > 0) tintColor = '#6cf';
    else if (m.dashT > 0) tintColor = '#ffe';
    const shown = img && tintColor ? renderer.tinted(img, tintColor, 0.45) : img;
    if (shown) {
      renderer.sprite(shown, this.x, this.y, { scale: 0.5 }); // 96px 素材按半尺寸显示
    } else {
      renderer.circle(this.x, this.y, 5, { fill: '#cdf', stroke: '#fff' });
    }
    // 低速：判定点 + 磁吸圈（玩法指示，保留）
    if (this.slow) {
      renderer.circle(this.x, this.y, this.r, { fill: '#fff', stroke: '#39f' });
      renderer.circle(this.x, this.y, this.magnetR, { stroke: '#6cf', alpha: 0.25 });
    }
  }
}
